// ================ Action types ================ //

import { addMarketplaceEntities } from 'ducks/marketplaceData.duck';
import { storableError } from 'util/errors';
import { denormalisedResponseEntities } from 'util/data';
import { createPromoCodeApi, updatePromoCodeApi } from '../api';
import { APPLY_TO_SPECIFIC_LISTINGS_YES } from '../config/constant';
import { fetchListings } from './fieldSelectListings.duck';

// ================ Reducer ================ //

const initialState = {
  createPromoCodeInProgress: false,
  createPromoCodeSuccess: false,
  createPromoCodeError: null,
  fetchPromoCodeInProgress: false,
  fetchPromoCodeSuccess: false,
  fetchPromoCodeError: null,
  currentPromoCode: {},
  createdPromoCodeRef: null,
};

const CREATE_PROMO_CODE_IN_PROGRESS = 'app/EditPromoCodePage/CREATE_PROMO_CODE_IN_PROGRESS';
const CREATE_PROMO_CODE_SUCCESS = 'app/EditPromoCodePage/CREATE_PROMO_CODE_SUCCESS';
const CREATE_PROMO_CODE_ERROR = 'app/EditPromoCodePage/CREATE_PROMO_CODE_ERROR';

const FETCH_PROMO_CODE_IN_PROGRESS = 'app/EditPromoCodePage/FETCH_PROMO_CODE_IN_PROGRESS';
const FETCH_PROMO_CODE_SUCCESS = 'app/EditPromoCodePage/FETCH_PROMO_CODE_SUCCESS';
const FETCH_PROMO_CODE_ERROR = 'app/EditPromoCodePage/FETCH_PROMO_CODE_ERROR';

const UPDATE_PROMO_CODE_IN_PROGRESS = 'app/EditPromoCodePage/UPDATE_PROMO_CODE_IN_PROGRESS';
const UPDATE_PROMO_CODE_SUCCESS = 'app/EditPromoCodePage/UPDATE_PROMO_CODE_SUCCESS';
const UPDATE_PROMO_CODE_ERROR = 'app/EditPromoCodePage/UPDATE_PROMO_CODE_ERROR';

const RESET_EDIT_PROMO_CODE_STATE = 'app/EditPromoCodePage/RESET_EDIT_PROMO_CODE_STATE';

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CREATE_PROMO_CODE_IN_PROGRESS:
      return {
        ...state,
        createPromoCodeInProgress: true,
        createPromoCodeSuccess: false,
        createPromoCodeError: null,
      };
    case CREATE_PROMO_CODE_SUCCESS:
      return {
        ...state,
        createdPromoCodeRef: payload,
        createPromoCodeInProgress: false,
        createPromoCodeSuccess: true,
      };
    case CREATE_PROMO_CODE_ERROR:
      return {
        ...state,
        createPromoCodeInProgress: false,
        createPromoCodeError: payload,
      };
    case FETCH_PROMO_CODE_IN_PROGRESS:
      return {
        ...state,
        fetchPromoCodeInProgress: true,
        fetchPromoCodeSuccess: false,
        fetchPromoCodeError: null,
      };
    case FETCH_PROMO_CODE_SUCCESS:
      return {
        ...state,
        currentPromoCode: payload,
        fetchPromoCodeInProgress: false,
        fetchPromoCodeSuccess: true,
        createPromoCodeInProgress: false,
        createPromoCodeSuccess: false,
        createPromoCodeError: null,
        updatePromoCodeInProgress: false,
        updatePromoCodeSuccess: false,
        updatePromoCodeError: null,
      };
    case FETCH_PROMO_CODE_ERROR:
      return {
        ...state,
        fetchPromoCodeInProgress: false,
        fetchPromoCodeError: payload,
      };
    case UPDATE_PROMO_CODE_IN_PROGRESS: {
      return {
        ...state,
        updatePromoCodeInProgress: true,
        updatePromoCodeSuccess: false,
        updatePromoCodeError: null,
      };
    }
    case UPDATE_PROMO_CODE_SUCCESS: {
      return {
        ...state,
        updatePromoCodeInProgress: false,
        updatePromoCodeSuccess: true,
      };
    }
    case UPDATE_PROMO_CODE_ERROR: {
      return {
        ...state,
        updatePromoCodeInProgress: false,
        updatePromoCodeError: payload,
      };
    }
    case RESET_EDIT_PROMO_CODE_STATE: {
      return {
        ...state,
        createPromoCodeInProgress: false,
        createPromoCodeSuccess: false,
        createPromoCodeError: null,
        fetchPromoCodeInProgress: false,
        fetchPromoCodeSuccess: false,
        fetchPromoCodeError: null,
      };
    }
    default:
      return state;
  }
}

// ================ Action creators ================ //

const createPromoCodeInProgress = () => ({
  type: CREATE_PROMO_CODE_IN_PROGRESS,
});

const createPromoCodeSuccess = (payload) => ({
  type: CREATE_PROMO_CODE_SUCCESS,
  payload,
});

const createPromoCodeError = (error) => ({
  type: CREATE_PROMO_CODE_ERROR,
  payload: error,
});

const fetchPromoCodeInProgress = () => ({
  type: FETCH_PROMO_CODE_IN_PROGRESS,
});

const fetchPromoCodeSuccess = (listing) => ({
  type: FETCH_PROMO_CODE_SUCCESS,
  payload: listing,
});

const fetchPromoCodeError = (error) => ({
  type: FETCH_PROMO_CODE_ERROR,
  payload: error,
});

const updatePromoCodeInProgress = () => ({
  type: UPDATE_PROMO_CODE_IN_PROGRESS,
});

const updatePromoCodeSuccess = (payload) => ({
  type: UPDATE_PROMO_CODE_SUCCESS,
  payload,
});

const updatePromoCodeError = (error) => ({
  type: UPDATE_PROMO_CODE_ERROR,
  payload: error,
});

export const resetEditPromoCodeState = () => ({
  type: RESET_EDIT_PROMO_CODE_STATE,
});

// ================ Thunks ================ //

const fetchPromoCode =
  ({ id }) =>
  async (dispatch, getState, sdk) => {
    try {
      dispatch(fetchPromoCodeInProgress());
      const response = await sdk.ownListings.show({
        id,
      });
      const listing = denormalisedResponseEntities(response);
      const { applicableListings } = listing[0].attributes.privateData;

      if (Array.isArray(applicableListings) && applicableListings.length > 0) {
        await dispatch(fetchListings({ listingIds: applicableListings }));
      }

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchPromoCodeSuccess(listing[0]));
    } catch (e) {
      dispatch(fetchPromoCodeError(storableError(e)));
    }
  };

export const createPromoCode =
  (data = {}) =>
  async (dispatch) => {
    try {
      dispatch(createPromoCodeInProgress());
      const response = await createPromoCodeApi(data);
      dispatch(addMarketplaceEntities(response));
      const promoCode = denormalisedResponseEntities(response)[0];
      const { id, type } = promoCode;
      dispatch(
        createPromoCodeSuccess({
          id,
          type,
        })
      );
      return id;
    } catch (e) {
      return dispatch(createPromoCodeError(storableError(e)));
    }
  };

export const updatePromoCode =
  (data = {}) =>
  async (dispatch) => {
    try {
      dispatch(updatePromoCodeInProgress());
      const response = await updatePromoCodeApi(data);
      dispatch(addMarketplaceEntities(response));
      dispatch(updatePromoCodeSuccess());
    } catch (e) {
      dispatch(updatePromoCodeError(storableError(e)));
    }
  };

export const loadData = (params) => async (dispatch) => {
  const { id } = params;
  if (id === 'new') {
    return dispatch(fetchPromoCodeSuccess({}));
  }
  return dispatch(fetchPromoCode({ id }));
};

export const selectCreatePromoCodeInProgress = (state) =>
  state.EditPromoCodePage.createPromoCodeInProgress;
export const selectCreatePromoCodeSuccess = (state) =>
  state.EditPromoCodePage.createPromoCodeSuccess;
export const selectCreatePromoCodeError = (state) => state.EditPromoCodePage.createPromoCodeError;

export const selectUpdatePromoCodeInProgress = (state) =>
  state.EditPromoCodePage.updatePromoCodeInProgress;
export const selectUpdatePromoCodeSuccess = (state) =>
  state.EditPromoCodePage.updatePromoCodeSuccess;
export const selectUpdatePromoCodeError = (state) => state.EditPromoCodePage.updatePromoCodeError;

export const selectCurrentPromoCode = (state) => state.EditPromoCodePage.currentPromoCode;
